/**
 * Call-session tracking, extracted from AmeyoToolbar.
 *
 * This is vendor-neutral: it accumulates the events of one call and posts them to
 * the CRM's own /calling/events endpoint. It lived inside the Ameyo toolbar
 * module purely for historical reasons, which meant anything needing session
 * tracking — including the NicePhone flow — had to import from the Ameyo file and
 * so kept that vendor's script, iframe markup and hardcoded credentials in the
 * bundle. Nothing here is Ameyo-specific.
 */
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';

// =========================================================
// CALL SESSION TRACKER
// =========================================================

/**
 * Global call session tracker for tracking call lifecycle events
 */
let activeCallSession = {
  crtObjectId: null,
  phone: null,
  agent: null,
  leadId: null,
  events: [],
  startedAt: null,
  callSource: null,
};

/**
 * Log a call lifecycle event
 * @param {string} status - Event status (e.g., "LOGIN", "READY", "HANGUP")
 * @param {object} userCustomerCRTInfo - vendor CRT info containing userCrtObjectId
 */
export function logCallEvent(status, userCustomerCRTInfo = null) {
  const timestamp = new Date().toISOString();
  console.log('logCallEvent', status, userCustomerCRTInfo);
  // Initialize session if we have CRT info and no active session
  if (!activeCallSession.crtObjectId && userCustomerCRTInfo?.customerCrtObjectId) {
    // Import dynamically to avoid circular dependency
    const callStore = require('../stores/callStore');
    const currentLead = callStore.useCallStore.getState().currentLead;
    
    activeCallSession.crtObjectId = userCustomerCRTInfo.customerCrtObjectId;
    activeCallSession.startedAt = timestamp;
    
    // Extract phone and agent if available
    if (userCustomerCRTInfo.phone) {
      activeCallSession.phone = userCustomerCRTInfo.phone;
    }
    if (userCustomerCRTInfo.agent) {
      activeCallSession.agent = userCustomerCRTInfo.agent;
    }
    
    // Capture leadId from current CRM state
    if (currentLead) {
      activeCallSession.leadId = currentLead.leadID || currentLead.id;
      console.log('📋 Captured lead ID from CRM state:', activeCallSession.leadId);
    }
    
    console.log('📞 Call session started:', {
      crtObjectId: activeCallSession.crtObjectId,
      phone: activeCallSession.phone,
      agent: activeCallSession.agent,
      leadId: activeCallSession.leadId
    });
  }

  // Add event to timeline
  activeCallSession.events.push({
    status,
    timestamp
  });

  console.log(`📝 Call Event Logged: ${status} at ${timestamp}`);
  console.log('Current session:', {
    crtObjectId: activeCallSession.crtObjectId,
    leadId: activeCallSession.leadId,
    eventCount: activeCallSession.events.length
  });
}

/**
 * Send call events to backend and reset session
 */
export async function finalizeCallSession() {
  if (!activeCallSession.crtObjectId || activeCallSession.events.length === 0) {
    console.log('⚠️ No active call session to finalize');
    return;
  }

  // Last-chance leadId resolution from the store
  if (!activeCallSession.leadId) {
    const callStore = require('../stores/callStore');
    const currentLead = callStore.useCallStore.getState().currentLead;
    if (currentLead) {
      activeCallSession.leadId = currentLead.leadID || currentLead.id;
      console.log('📋 Resolved leadId from store at finalize:', activeCallSession.leadId);
    }
  }

  // Last-chance agent resolution from auth state
  if (!activeCallSession.agent) {
    try {
      const { store } = require('../store');
      const user = store.getState()?.auth?.user;
      if (user) {
        activeCallSession.agent = String(user.userID || user.userCode);
        console.log('📋 Resolved agent from auth at finalize:', activeCallSession.agent);
      }
    } catch (e) { /* store not available */ }
  }

  const payload = {
    crtObjectId: activeCallSession.crtObjectId,
    events: activeCallSession.events,
    startedAt: activeCallSession.startedAt,
    endedAt: new Date().toISOString(),
    phone: activeCallSession.phone,
    agent: activeCallSession.agent,
    leadId: activeCallSession.leadId,
    callSource: activeCallSession.callSource,
  };

  console.log('📤 Sending call events to backend:', payload);

  try {
    const response = await axiosInstance.post(API_ENDPOINTS.saveCallEvents, payload);
    console.log('✅ Call events saved successfully:', response.data);
    
    // Reset session after successful save
    activeCallSession = {
      crtObjectId: null,
      phone: null,
      agent: null,
      leadId: null,
      events: [],
      startedAt: null,
      callSource: null,
    };
    
    console.log('🔄 Call session reset');
  } catch (error) {
    console.error('❌ Failed to save call events:', error);
    // Don't throw - we don't want to break the CRM flow
    // Events will be lost but call flow continues
  }
}

/**
 * Explicitly initialize a call session with a known crtObjectId.
 * Called from Layout.jsx showCrm handler where requestId = crtObjectId.
 */
export async function initCallSession(crtObjectId, phone) {
  if (!crtObjectId) return;
  
  const callStore = require('../stores/callStore');
  const currentLead = callStore.useCallStore.getState().currentLead;

  activeCallSession.crtObjectId = crtObjectId;
  activeCallSession.startedAt = activeCallSession.startedAt || new Date().toISOString();

  if (phone) {
    activeCallSession.phone = String(phone).replace(/\D/g, '').slice(-10);
  }
  if (currentLead) {
    activeCallSession.leadId = currentLead.leadID || currentLead.id;
  }

  // Resolve agent from Redux auth state
  if (!activeCallSession.agent) {
    try {
      const { store } = require('../store');
      const user = store.getState()?.auth?.user;
      if (user) {
        activeCallSession.agent = String(user.userID || user.userCode);
      }
    } catch (e) { /* store not available */ }
  }

  console.log('📞 Call session initialized via initCallSession:', {
    crtObjectId: activeCallSession.crtObjectId,
    leadId: activeCallSession.leadId,
    startedAt: activeCallSession.startedAt,
  });

  if (!activeCallSession.leadId) {
    console.log('⏳ Deferring initial save — leadId not available yet, will be sent with finalizeCallSession');
    return;
  }

  try {
    const response = await axiosInstance.post(API_ENDPOINTS.saveCallEvents, {
      crtObjectId: activeCallSession.crtObjectId,
      leadId: activeCallSession.leadId,
      startedAt: activeCallSession.startedAt,
      callSource: activeCallSession.callSource,
      events: [
        {
          status: 'SHOW_CRM',
          timestamp: activeCallSession.startedAt
        }
      ]
    });
    console.log('✅ Call events saved successfully:', response.data);
  } catch (error) {
    console.error('❌ Failed to initialize call session:', error);
  }
}

/**
 * Get current active call session (for debugging)
 */
export function getActiveCallSession() {
  return { ...activeCallSession };
}

export function setCallSessionSource(source) {
  activeCallSession.callSource = source;
}

export function setCallSessionLeadId(leadId) {
  if (leadId) activeCallSession.leadId = leadId;
}
